import { useGetDriverFaceCluster } from '@cobliteam/hooks/src/useCamera/useDriverFaceCluster';
import { useTranslation } from '@cobliteam/i18n';
import FileUpload, { FileUploadPreview } from '@cobliteam/ui/components/FileUpload';
import Popover from '@cobliteam/ui/components/Popover';
import Skeleton from '@cobliteam/ui/components/Skeleton';
import Typography from '@cobliteam/ui/components/Typography';
import Icon from '@cobliteam/ui/icons';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { DRIVER_FACE_PHOTO_MAX } from '../constants';
import type { FaceFileMetadata, StagedFaceImageUpdate } from '../types';
import { DriverFaceIdEmptyBanner } from './DriverFaceIdEmptyBanner/DriverFaceIdEmptyBanner';

export interface DriverPhotosUploadRef {
  hasChanges: () => boolean;
  handleSubmit: () => void;
}

interface DriverPhotosUploadProps {
  driverId: string;
  onAddMoreClusters: () => void;
  disabled?: boolean;
  /**
   * Called when the user confirms the legacy photos step. Receives the staged delete plan
   * so DriverDrawer can route through `saveWithLegacyImages` → `assignDriverFaceImage`.
   */
  onFinish?: (updates: StagedFaceImageUpdate) => void;
}

const buildPhotos = (sampleUrls: Array<{ imageUrl: string } | null | undefined>): FaceFileMetadata[] =>
  sampleUrls.flatMap((sample) => {
    if (!sample) return [];
    const { imageUrl } = sample;
    return [
      {
        url: imageUrl,
        id: imageUrl,
        name: imageUrl,
        size: 0,
        type: '',
        faceImageUrl: imageUrl,
      },
    ];
  });

const DriverPhotosUpload = forwardRef<DriverPhotosUploadRef, DriverPhotosUploadProps>(({ driverId, onAddMoreClusters, disabled, onFinish }, ref) => {
  const { t } = useTranslation('drivers');
  const { data: driverFaceCluster, isLoading } = useGetDriverFaceCluster(driverId, { enabled: !!driverId });

  const sampleUrls = driverFaceCluster?.sampleImageUrls ?? [];
  const initialPhotos = useMemo<FaceFileMetadata[]>(() => buildPhotos(sampleUrls), [sampleUrls]);

  const [photos, setPhotos] = useState<FaceFileMetadata[]>(initialPhotos);

  // Re-sync from server on data refetch — covers the initial load (data: undefined → loaded)
  // and any background refetch that brings new sampleImageUrls. The user's pending removals
  // for a still-existing photo would be reset here, but in practice a refetch happens after
  // a save (which removed the photo server-side anyway) or after the user reopens the drawer.
  useEffect(() => {
    setPhotos(initialPhotos);
  }, [initialPhotos]);

  const removedFiles = useMemo(() => initialPhotos.filter((p) => !photos.some((lp) => lp.id === p.id)), [initialPhotos, photos]);

  const hasChanges = useCallback(() => removedFiles.length > 0, [removedFiles]);

  const handleSubmit = useCallback(() => {
    const update: StagedFaceImageUpdate = {
      changedAssignments: [],
      removedAssignments: removedFiles.map((f) => ({ url: f.faceImageUrl ?? f.url })),
      aiFiles: [],
      uploadFiles: [],
      previewUrls: [],
    };
    onFinish?.(update);
  }, [removedFiles, onFinish]);

  useImperativeHandle(ref, () => ({ hasChanges, handleSubmit }), [hasChanges, handleSubmit]);

  if (isLoading) {
    return (
      <div className='space-y-4'>
        <div className='flex items-center gap-2'>
          <Skeleton height={20} width={20} className='rounded' />
          <Skeleton height={20} width={200} />
          <Skeleton height={16} width={16} className='rounded-full' />
        </div>

        <div className='flex items-center justify-between gap-2 rounded-lg border border-gray-3 p-4'>
          <div className='flex items-center gap-2'>
            <Skeleton height={36} width={36} className='rounded-full' />
            <Skeleton height={20} width={240} />
          </div>
          <Skeleton height={16} width={16} className='rounded-full' />
        </div>

        <div className='mt-2 flex flex-wrap gap-4'>
          {['skeleton-1', 'skeleton-2', 'skeleton-3', 'skeleton-4', 'skeleton-5', 'skeleton-6'].map((skeletonId) => (
            <Skeleton key={skeletonId} className='size-28 rounded-lg' />
          ))}
        </div>

        <div className='overflow-hidden rounded-lg border border-gray-3'>
          <Skeleton className='aspect-[744/108] w-full rounded-none' />
          <div className='space-y-4 p-4'>
            <Skeleton height={20} width='70%' />
            <div className='flex items-center gap-4'>
              <Skeleton height={36} width={160} className='rounded-md' />
              <Skeleton height={16} width={180} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-4'>
      <div className='flex items-center gap-2'>
        <Icon name='video' size='md' />
        <Typography variant='text-md-semibold'>{t('face_id.drawer.review.photos_count', { count: photos.length, max: DRIVER_FACE_PHOTO_MAX })}</Typography>
        <Popover>
          <Popover.Trigger>
            <Popover.Icon />
          </Popover.Trigger>
          <Popover.Content className='w-60 space-y-2'>
            <Typography variant='text-md-semibold'>{t('face_id.drawer.review.photos_popover_title')}</Typography>
            <Typography variant='text-md' color='secondary'>
              {t('face_id.drawer.review.photos_popover_description')}
            </Typography>
          </Popover.Content>
        </Popover>
      </div>

      <div className='flex items-center justify-between gap-2 rounded-lg border border-gray-3 p-4'>
        <div className='flex items-center gap-2'>
          <div className='flex items-center justify-center rounded-full bg-gray-3 p-2'>
            <Icon name='camera' color='primary' />
          </div>
          <Typography variant='text-md-semibold'>{t('face_id.drawer.review.camera_only_banner_title')}</Typography>
        </div>
        <Popover>
          <Popover.Trigger>
            <Popover.Icon />
          </Popover.Trigger>
          <Popover.Content className='w-60 space-y-2'>
            <Typography variant='text-md-semibold'>{t('face_id.drawer.review.camera_only_banner_popover_title')}</Typography>
            <Typography variant='text-md' color='secondary'>
              {t('face_id.drawer.review.camera_only_banner_popover_description')}
            </Typography>
            <Typography variant='text-md' color='secondary'>
              {t('face_id.drawer.review.legacy_photos_notice')}
            </Typography>
          </Popover.Content>
        </Popover>
      </div>

      <FileUpload files={photos} onFilesChange={setPhotos}>
        <FileUploadPreview />
      </FileUpload>

      <DriverFaceIdEmptyBanner
        onLinkPhotosClick={onAddMoreClusters}
        disabled={disabled}
        title={t('face_id.drawer.review.add_more_title')}
        subtitle=''
        helper={t('face_id.drawer.review.add_more_description')}
      />
    </div>
  );
});

DriverPhotosUpload.displayName = 'DriverPhotosUpload';

export default DriverPhotosUpload;
